import { Shield, Lock, AlertTriangle, Eye, CreditCard, Users } from "lucide-react";

const CyberAwarenessSection = () => {
  const threats = [
    {
      icon: AlertTriangle,
      title: "Phishing Attacks",
      description: "Fake emails and websites tricking users into revealing personal details. Always verify sender information.",
      color: "#FF3B30"
    },
    {
      icon: Lock,
      title: "Ransomware",
      description: "Malicious software that locks your files until payment is made. Keep backups and use antivirus.",
      color: "#FFD700"
    },
    {
      icon: Eye,
      title: "Data Breaches",
      description: "Hackers stealing sensitive data through weak security. Enable two-factor authentication.",
      color: "#007AFF"
    },
    {
      icon: Users,
      title: "Social Engineering",
      description: "Psychological manipulation to gain confidential information. Always verify unknown requests.",
      color: "#00C853"
    },
    {
      icon: CreditCard,
      title: "Financial Fraud",
      description: "UPI scams, fake investment schemes, and payment frauds. Never share OTPs or PINs.",
      color: "#FF9500"
    },
    {
      icon: Shield,
      title: "Identity Theft",
      description: "Personal information used for fraudulent activities. Monitor your accounts regularly.",
      color: "#AF52DE"
    }
  ];

  const stats = [
    { value: "₹1,750 Cr", label: "Lost to cyber fraud in 2023" },
    { value: "66%", label: "Increase in reported cases" },
    { value: "4.5L+", label: "Complaints registered" },
    { value: "1 in 3", label: "Indians targeted by scams" }
  ];

  return (
    <section className="cyber-section section-spacing relative overflow-hidden" data-testid="cyber-section">
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#FFD700] text-sm uppercase tracking-[0.2em] font-bold mb-4">
            Why We Run
          </p>
          <h2 className="text-4xl sm:text-5xl font-bold text-white font-['Outfit'] mb-4">
            Cyber Crime <span className="text-[#FF3B30]">Awareness</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            India faces an unprecedented cybercrime crisis. The FinRun exists to raise awareness 
            and empower every citizen to protect themselves in the digital world.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat) => (
            <div 
              key={stat.label} 
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center"
            >
              <p className="text-3xl md:text-4xl font-black font-['Outfit'] text-[#FFD700] mb-2">
                {stat.value}
              </p>
              <p className="text-gray-400 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Threats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {threats.map((threat, i) => {
            const Icon = threat.icon;
            return (
              <div
                key={i}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all group"
                data-testid={`threat-card-${i}`}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${threat.color}20` }}
                >
                  <Icon className="w-7 h-7" style={{ color: threat.color }} />
                </div>
                <h3 className="text-xl font-bold text-white font-['Outfit'] mb-3">
                  {threat.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {threat.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Safety Tips */}
        <div className="mt-16 bg-gradient-to-r from-[#FF3B30]/20 to-[#007AFF]/20 border border-white/10 rounded-3xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] mb-4">
                Stay Secure Online
              </h3>
              <ul className="space-y-3 text-gray-300">
                {[
                  "Use strong, unique passwords for every account",
                  "Enable two-factor authentication (2FA)",
                  "Never share OTPs, PINs, or passwords",
                  "Verify before clicking unknown links",
                  "Keep software and devices updated",
                  "Report suspicious activities immediately"
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#00C853]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#00C853] text-xs font-bold">{i + 1}</span>
                    </div>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center">
              <div className="inline-block bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
                <p className="text-6xl md:text-7xl font-black font-['Outfit'] text-[#FFD700] mb-2">
                  1930
                </p>
                <p className="text-white font-semibold mb-1">National Cyber Crime Helpline</p>
                <p className="text-gray-400 text-sm">Report fraud immediately</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CyberAwarenessSection;
